/**
 * Power level utility functions for Matrix room permissions
 * 
 * Matrix power levels follow a strict hierarchy where users can only
 * perform actions on users with strictly lower power levels.
 * 
 * Reference: Matrix Spec v1.11 - Room Authorization Rules
 * https://spec.matrix.org/v1.11/rooms/v11/#authorization-rules
 */

/**
 * Determines if a user can remove (kick) another user from a room based on power levels
 * 
 * According to Matrix spec Room v11, section 4.5:
 * For membership='leave' (kick), the target user's power level must be
 * LESS THAN the sender's power level (strict inequality)
 * 
 * @param {number|undefined} senderPowerLevel - Power level of the user attempting removal
 * @param {number|undefined} targetPowerLevel - Power level of the user to be removed
 * @returns {boolean} - True if removal is allowed, false if blocked
 * 
 * @example
 * canRemoveUser(50, 0)   // true  - moderator can remove default user
 * canRemoveUser(50, 50)  // false - cannot remove user with equal power
 * canRemoveUser(50, 100) // false - cannot remove user with higher power
 * canRemoveUser(0, -10)  // true  - default can remove restricted user
 */
export const canRemoveUser = (senderPowerLevel, targetPowerLevel) => {
  // Default power level is 0 if undefined
  const sender = senderPowerLevel ?? 0;
  const target = targetPowerLevel ?? 0;
  
  // Can only remove if target power level is strictly less than sender's
  return target < sender;
};

/**
 * Determines if removal should be blocked based on power levels
 * This is the inverse of canRemoveUser for convenience in UI logic
 * 
 * @param {number|undefined} senderPowerLevel - Power level of the user attempting removal
 * @param {number|undefined} targetPowerLevel - Power level of the user to be removed
 * @returns {boolean} - True if removal should be blocked, false if allowed
 * 
 * @example
 * shouldBlockRemoval(50, 0)   // false - removal is allowed
 * shouldBlockRemoval(50, 50)  // true  - removal is blocked (equal power)
 * shouldBlockRemoval(50, 100) // true  - removal is blocked (higher power)
 */
export const shouldBlockRemoval = (senderPowerLevel, targetPowerLevel) => {
  return !canRemoveUser(senderPowerLevel, targetPowerLevel);
};

/**
 * Gets the default power level for a user if not specified
 * According to Matrix spec, default power level is 0
 * 
 * @param {number|undefined} powerLevel - The power level value
 * @returns {number} - The power level or 0 if undefined
 */
export const getDefaultPowerLevel = (powerLevel) => {
  return powerLevel ?? 0;
};

/**
 * Compares two power levels
 * 
 * @param {number|undefined} powerLevel1 - First power level to compare
 * @param {number|undefined} powerLevel2 - Second power level to compare
 * @returns {number} - Negative if pl1 < pl2, 0 if equal, positive if pl1 > pl2
 */
export const comparePowerLevels = (powerLevel1, powerLevel2) => {
  const pl1 = getDefaultPowerLevel(powerLevel1);
  const pl2 = getDefaultPowerLevel(powerLevel2);
  return pl1 - pl2;
};

/**
 * Common power level constants used in Matrix
 */
export const POWER_LEVELS = {
  RESTRICTED: -10,  // User with limited permissions
  DEFAULT: 0,       // Default user
  MODERATOR: 50,    // Room moderator
  ADMIN: 100,       // Room administrator
};
